/**
 * VendorBadgeSubscriber — pushes pendingVendors / draftProducts /
 * pendingRatingResponses via SignalR.
 *
 * Phase 4 of the badge migration. Connects to the `suppliers` hub.
 * Currently only the pendingVendors counter has a consumer
 * (VendorStatusChangedBadgeConsumer); the other two stay on the REST
 * baseline until matching events / consumers are added.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useVendorService,
  useVendorProductService,
  useVendorRatingService,
  useVendorResponseService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useVendorBadgesStore } from './vendorBadgesStore';

const VENDOR_ROLES = ['Admin'];
const PENDING_VENDOR_STATUSES = new Set([1, 2]);
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[VendorBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

interface VendorDeltas {
  pendingVendors?: number;
  draftProducts?: number;
  pendingRatingResponses?: number;
}

export function VendorBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(VENDOR_ROLES);

  const vendorService = useVendorService();
  const productService = useVendorProductService();
  const ratingService = useVendorRatingService();
  const responseService = useVendorResponseService();
  const setFromMetrics = useVendorBadgesStore((s) => s.setFromMetrics);
  const applyDelta = useVendorBadgesStore((s) => s.applyDelta);
  const reset = useVendorBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      const [vendors, products, ratings, responses] = await Promise.all([
        vendorService.getViews() as Promise<any[]>,
        productService.getViews() as Promise<any[]>,
        ratingService.getViews() as Promise<any[]>,
        responseService.getAll() as Promise<any[]>,
      ]);
      const pendingVendors = (vendors ?? []).filter((v: any) =>
        PENDING_VENDOR_STATUSES.has(Number(v.vendorStatusID)),
      ).length;
      const draftProducts = (products ?? []).filter((p: any) => Number(p.statusID) === -1).length;
      const respondedIds = new Set((responses ?? []).map((r: any) => r.ratingID));
      const pendingRatingResponses = (ratings ?? []).filter(
        (r: any) => !respondedIds.has(r.ratingID),
      ).length;

      log('baseline', { pendingVendors, draftProducts, pendingRatingResponses });
      setFromMetrics({ pendingVendors, draftProducts, pendingRatingResponses });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [vendorService, productService, ratingService, responseService, setFromMetrics]);

  useEffect(() => {
    if (!enabled) return;
    refetchBaseline();
  }, [enabled, refetchBaseline]);

  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('suppliers', {
    autoConnect: enabled,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<VendorDeltas>) => {
      if (msg.domain !== 'Suppliers') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<VendorDeltas>) => {
      if (msg.domain !== 'suppliers') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [enabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { enabled });
  }, [enabled]);

  return null;
}
